/** @jsx h */
import Sortable from 'sortablejs';
import { h } from '../../../../utils/domJsx';
import { openEditorModal } from '../../../../services/editorModal';
import { openBulkImageSelect } from './bulkImageSelect';
import './slideSorter.less';

const DragHandleIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        width="18px"
        height="18px"
    >
        <circle cx="9" cy="6" r="1" />
        <circle cx="9" cy="12" r="1" />
        <circle cx="9" cy="18" r="1" />
        <circle cx="15" cy="6" r="1" />
        <circle cx="15" cy="12" r="1" />
        <circle cx="15" cy="18" r="1" />
    </svg>
);

const TrashIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        width="18px"
        height="18px"
    >
        <path d="M3 6h18" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
    </svg>
);

const findImageSource = (model) => {
    if (!model) return null;

    const tagName = (model.get('tagName') || '').toLowerCase();
    const attrs = model.getAttributes ? model.getAttributes() : {};

    if (tagName === 'img' && attrs.src) {
        return attrs.src;
    }

    const style = (model.getStyle && model.getStyle()) || {};
    const bg = style['background-image'] || style.background || '';
    const match = /url\(\s*['"]?([^'")]+)['"]?\s*\)/i.exec(bg);
    if (match && match[1]) {
        return match[1];
    }

    const children = model.components ? model.components() : null;
    if (children && children.length) {
        for (let i = 0; i < children.length; i += 1) {
            const found = findImageSource(children.at(i));
            if (found) return found;
        }
    }

    return null;
};

const SlideThumb = ({ model, index }) => {
    const label = `Folie ${index + 1}`;
    const src = findImageSource(model);

    if (!src) {
        return (
            <div className="sio-slide-sorter__thumb sio-slide-sorter__thumb--fallback">
                {label}
            </div>
        );
    }

    return (
        <div className="sio-slide-sorter__thumb">
            <img
                src={src}
                alt={label}
                loading="lazy"
                onError={(event) => {
                    const thumb = event.target.parentElement;
                    thumb.classList.add('sio-slide-sorter__thumb--fallback');
                    thumb.textContent = label;
                    event.target.remove();
                }}
            />
        </div>
    );
};

const SlideRow = ({ model, index }) => (
    <div className="sio-slide-sorter__item" data-cid={model.cid}>
        <span className="sio-slide-sorter__handle" aria-label="Drag to reorder">
            <DragHandleIcon />
        </span>
        <button
            type="button"
            className="sio-slide-sorter__remove"
            title="Remove slide"
            aria-label="Remove slide"
        >
            <TrashIcon />
        </button>
        <SlideThumb model={model} index={index} />
        <div className="sio-slide-sorter__label">
            <span className="sio-slide-sorter__label-index">{`Folie ${index + 1}`}</span>
        </div>
    </div>
);

const buildSlideSorterView = (models) => {
    if (!models.length) {
        return {
            container: (
                <div className="sio-slide-sorter">
                    <div className="sio-slide-sorter__empty">
                        This slider has no slides yet.
                    </div>
                </div>
            ),
            list: null,
        };
    }

    let list = null;

    const container = (
        <div className="sio-slide-sorter">
            <div
                className="sio-slide-sorter__list"
                ref={(el) => {
                    list = el;
                }}
            >
                {models.map((model, index) => (
                    <SlideRow model={model} index={index} />
                ))}
            </div>
        </div>
    );

    return { container, list };
};

const refreshIndexLabels = (listEl) => {
    const rows = listEl.querySelectorAll('.sio-slide-sorter__item');
    rows.forEach((row, i) => {
        const idx = row.querySelector('.sio-slide-sorter__label-index');
        if (idx) idx.textContent = `Folie ${i + 1}`;
        const fallback = row.querySelector('.sio-slide-sorter__thumb--fallback');
        if (fallback && !fallback.querySelector('img')) {
            fallback.textContent = `Folie ${i + 1}`;
        }
    });
};

// Syncs the current DOM rows (order + removals) back into the collection.
const commitListToCollection = (list, slidesCollection, onApplied) => {
    const orderedModels = Array.from(
        list.querySelectorAll('.sio-slide-sorter__item')
    )
        .map((row) =>
            slidesCollection.models.find((m) => m.cid === row.dataset.cid)
        )
        .filter(Boolean);

    if (!orderedModels.length) return false;

    const keptCids = new Set(orderedModels.map((m) => m.cid));
    const removedModels = slidesCollection.models.filter(
        (m) => !keptCids.has(m.cid)
    );

    let changed = false;
    if (removedModels.length) {
        removedModels.forEach((model) => {
            if (typeof model.remove === 'function') {
                model.remove();
            } else {
                slidesCollection.remove(model);
            }
        });
        changed = true;
    }

    const reordered = applyOrder(slidesCollection, orderedModels);
    changed = changed || reordered;

    if (changed) {
        slidesCollection.trigger('sort');
        if (typeof onApplied === 'function') onApplied();
    }

    return changed;
};

const applyOrder = (slidesCollection, orderedModels) => {
    const currentOrder = slidesCollection.models.map((m) => m.cid);
    const nextOrder = orderedModels.map((m) => m.cid);

    if (currentOrder.join('|') === nextOrder.join('|')) return false;

    orderedModels.forEach((model) => {
        slidesCollection.remove(model, { silent: true });
    });
    orderedModels.forEach((model, i) => {
        const isLast = i === orderedModels.length - 1;
        slidesCollection.add(model, { at: i, silent: !isLast });
    });

    return true;
};

export const openSlideSorter = async ({
    slidesCollection,
    config = {},
    onApplied,
} = {}) => {
    if (!slidesCollection) return false;

    const models = [...slidesCollection.models];
    const { container, list } = buildSlideSorterView(models);

    const uploadButton = {
        text: 'Mehrere Bilder hochladen',
        variant: 'secondary',
        autoClose: false,
        onClick: async (close) => {
            if (list) {
                commitListToCollection(list, slidesCollection, onApplied);
            }
            close({ confirmed: false, action: 'reopen', value: null });
            const added = await openBulkImageSelect({
                slidesCollection,
                config,
            });
            if (added.length) {
                openSlideSorter({ slidesCollection, config, onApplied });
            }
            return false;
        },
    };

    if (!list) {
        await openEditorModal({
            title: 'Folien verwalten',
            content: container,
            width: 640,
            showConfirm: false,
            cancelText: 'Close',
            extraFooterButtons: [uploadButton],
        });
        return false;
    }

    list.addEventListener('click', (event) => {
        const btn = event.target.closest('.sio-slide-sorter__remove');
        if (!btn) return;
        event.preventDefault();
        const rows = list.querySelectorAll('.sio-slide-sorter__item');
        if (rows.length <= 1) return;
        const row = btn.closest('.sio-slide-sorter__item');
        if (row) row.remove();
        refreshIndexLabels(list);
    });

    const sortable = Sortable.create(list, {
        animation: 160,
        handle: '.sio-slide-sorter__handle',
        draggable: '.sio-slide-sorter__item',
        ghostClass: 'sio-slide-sorter__item--ghost',
        chosenClass: 'sio-slide-sorter__item--chosen',
        dragClass: 'sio-slide-sorter__item--drag',
        forceFallback: true,
        fallbackOnBody: true,
        onEnd: () => refreshIndexLabels(list),
    });

    const result = await openEditorModal({
        title: 'Folien verwalten',
        content: container,
        width: 780,
        confirmText: 'Speichern',
        cancelText: 'Abbrechen',
        extraFooterButtons: [uploadButton],
        onConfirm: () => {
            commitListToCollection(list, slidesCollection, onApplied);
            return true;
        },
    });

    sortable.destroy();
    return !!result.confirmed;
};

export default openSlideSorter;
